import { useAuth } from 'src/auth/AuthContext';
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Box,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Button,
  Stack,
  Popover,
  IconButton,
  Collapse,
  Divider,
  List,
  ListItem,
  LinearProgress
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Warning,
  Info,
  Payment,
  Business,
  Edit,
  FilterList,
  Clear,
  TrendingUp,
  HelpOutline,
  ExpandMore,
  Refresh
} from '@mui/icons-material';
import { Subscription } from '../../../services/subscriptionService';
import { useSubscriptions, useSyncSubscriptions } from 'src/queries/subscriptions';

type SortableColumn = 
  | 'merchantName' 
  | 'merchantId' 
  | 'type' 
  | 'brewprksStatus' 
  | 'payfastStatus' 
  | 'amount' 
  | 'billingDate' 
  | 'createdAt' 
  | 'updatedAt'
  | 'trialProgress';

type SortDirection = 'asc' | 'desc';

const SubscriptionAdminPage: React.FC = () => {
  const [actionError, setActionError] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<SortableColumn>('createdAt');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [brewprksStatusFilter, setBrewprksStatusFilter] = useState<string[]>([]);
  const [payfastStatusFilter, setPayfastStatusFilter] = useState<string[]>([]);
  const [mrrPopoverAnchor, setMrrPopoverAnchor] = useState<HTMLElement | null>(null);
  const [mrrExpanded, setMrrExpanded] = useState(false);
  const [conversionPopoverAnchor, setConversionPopoverAnchor] = useState<HTMLElement | null>(null);
  const [selectedTrialSubscription, setSelectedTrialSubscription] = useState<Subscription | null>(null);

  const user = useAuth();

  const {
    data: subscriptions = [],
    isPending,
    isFetching,
    isError,
    dataUpdatedAt,
  } = useSubscriptions();
  const syncSubscriptions = useSyncSubscriptions();

  const isLoading = isFetching || syncSubscriptions.isPending;
  const error = isError
    ? 'Failed to load subscription data. Please try again later.'
    : actionError;

  // The old page showed the age of a localStorage cache entry it managed itself.
  // `dataUpdatedAt` is the same idea but honest: the moment this data actually
  // arrived, and it cannot outlive a sign-out the way the stored copy could.
  const dataAgeSeconds = dataUpdatedAt
    ? Math.floor((Date.now() - dataUpdatedAt) / 1000)
    : null;

  const handleRefresh = async () => {
    setActionError(null);
    try {
      // Pull PayFast's current state, then let the invalidation refetch the list.
      await syncSubscriptions.mutateAsync();
    } catch (err) {
      setActionError('Failed to sync subscription data. Please try again later.');
      console.error('Error syncing subscriptions:', err);
    }
  };

  const handleSortRequest = (column: SortableColumn) => {
    const isAsc = sortColumn === column && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const getSortValue = (subscription: Subscription, column: SortableColumn): any => {
    switch (column) {
      case 'merchantName':
        return subscription.merchant.name.toLowerCase();
      case 'merchantId':
        return subscription.merchant_id;
      case 'type':
        if (subscription.pf_status !== null) return 'payfast';
        if (subscription.subscription_token.includes('tmp')) return 'manual';
        return 'local';
      case 'brewprksStatus':
        return subscription.status.toLowerCase();
      case 'payfastStatus':
        return subscription.pf_status?.toLowerCase() || 'zzz'; // N/A goes to end
      case 'amount':
        return subscription.pf_amount || 0;
      case 'billingDate':
        return new Date(subscription.billing_date).getTime();
      case 'createdAt':
        return new Date(subscription.createdAt).getTime();
      case 'updatedAt':
        return new Date(subscription.updatedAt).getTime();
      case 'trialProgress':
        // Sort by stamp count, with non-trial subscriptions at the end
        if (subscription.status !== 'FREE_TRIAL' || typeof subscription.stampCount !== 'number') {
          return -1; // Non-trial subscriptions go to the end when sorting desc
        }
        return subscription.stampCount;
      default:
        return '';
    }
  };

  const sortedSubscriptions = [...subscriptions].sort((a, b) => {
    const aValue = getSortValue(a, sortColumn);
    const bValue = getSortValue(b, sortColumn);
    
    if (aValue < bValue) {
      return sortDirection === 'asc' ? -1 : 1;
    }
    if (aValue > bValue) {
      return sortDirection === 'asc' ? 1 : -1;
    }
    return 0;
  });

  // Get unique status values for filters
  const uniqueBrewprksStatuses = Array.from(new Set(subscriptions.map(s => s.status))).sort();
  const uniquePayfastStatuses = Array.from(new Set(
    subscriptions
      .filter(s => s.pf_status !== null)
      .map(s => s.pf_status!)
  )).sort();
  
  // Add "N/A" to PayFast statuses for non-PayFast subscriptions
  const allPayfastStatuses = subscriptions.some(s => s.pf_status === null) 
    ? ['N/A', ...uniquePayfastStatuses] 
    : uniquePayfastStatuses;

  // Apply filters
  const filteredAndSortedSubscriptions = sortedSubscriptions.filter(subscription => {
    // BrewPrks status filter
    const brewprksStatusMatch = brewprksStatusFilter.length === 0 || 
      brewprksStatusFilter.includes(subscription.status);
    
    // PayFast status filter
    const payfastStatus = subscription.pf_status || 'N/A';
    const payfastStatusMatch = payfastStatusFilter.length === 0 || 
      payfastStatusFilter.includes(payfastStatus);
    
    return brewprksStatusMatch && payfastStatusMatch;
  });

  const handleBrewprksStatusFilterChange = (event: any) => {
    const value = event.target.value;
    setBrewprksStatusFilter(typeof value === 'string' ? value.split(',') : value);
  };

  const handlePayfastStatusFilterChange = (event: any) => {
    const value = event.target.value;
    setPayfastStatusFilter(typeof value === 'string' ? value.split(',') : value);
  };

  const clearAllFilters = () => {
    setBrewprksStatusFilter([]);
    setPayfastStatusFilter([]);
  };

  const hasActiveFilters = brewprksStatusFilter.length > 0 || payfastStatusFilter.length > 0;

  const getBrewPrksStatusChip = (subscription: Subscription) => {
    const status = subscription.status;
    let color: 'success' | 'error' | 'warning' | 'info' | 'default' = 'default';
    let icon = <Info />;

    switch (status) {
      case 'ACTIVE':
        color = 'success';
        icon = <CheckCircle />;
        break;
      case 'INACTIVE':
      case 'CANCELLED':
        color = 'error';
        icon = <Cancel />;
        break;
      case 'PAUSED':
        color = 'warning';
        icon = <Warning />;
        break;
      default:
        color = 'info';
    }

    return (
      <Chip
        label={status}
        color={color}
        size="small"
        icon={icon}
        sx={{ minWidth: 100 }}
      />
    );
  };

  const getPayfastStatusChip = (subscription: Subscription) => {
    if (subscription.pf_status === null) {
      return (
        <Chip
          label="N/A"
          color="default"
          size="small"
          variant="outlined"
          sx={{ minWidth: 100 }}
        />
      );
    }

    const payfastStatus = subscription.pf_status;
    const statusReason = subscription.pf_status_reason;
    let color: 'success' | 'error' | 'warning' | 'info' | 'default' = 'default';
    let icon = <Info />;

    switch (payfastStatus) {
      case 'ACTIVE':
        color = 'success';
        icon = <CheckCircle />;
        break;
      case 'CANCELLED':
      case 'SUSPENDED':
        color = 'error';
        icon = <Cancel />;
        break;
      case 'PAUSED':
        color = 'warning';
        icon = <Warning />;
        break;
      default:
        color = 'info';
    }

    const chipElement = (
      <Chip
        label={payfastStatus}
        color={color}
        size="small"
        icon={icon}
        sx={{ minWidth: 100 }}
      />
    );

    // Add tooltip if there's a status reason
    if (statusReason && statusReason.trim()) {
      return (
        <Tooltip title={`Reason: ${statusReason}`} arrow>
          {chipElement}
        </Tooltip>
      );
    }

    return chipElement;
  };

  const formatAmount = (amount: number) => {
    return `R${(amount / 100).toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Helper function to calculate estimated conversion date
  const calculateConversionEstimate = (subscription: Subscription) => {
    if (subscription.status !== 'FREE_TRIAL' || typeof subscription.stampCount !== 'number') {
      return null;
    }

    const createdDate = new Date(subscription.createdAt);
    const currentDate = new Date();
    const daysSinceCreation = Math.max(1, Math.floor((currentDate.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24)));
    
    const currentStamps = subscription.stampCount;
    const maxStamps = 500;
    const remainingStamps = maxStamps - currentStamps;
    
    if (remainingStamps <= 0) {
      return { daysUntilConversion: 0, estimatedDate: currentDate, averageStampsPerDay: currentStamps / daysSinceCreation };
    }
    
    const averageStampsPerDay = currentStamps / daysSinceCreation;
    
    if (averageStampsPerDay <= 0) {
      return { daysUntilConversion: Infinity, estimatedDate: null, averageStampsPerDay: 0 };
    }
    
    const daysUntilConversion = Math.ceil(remainingStamps / averageStampsPerDay);
    const estimatedDate = new Date(currentDate.getTime() + (daysUntilConversion * 24 * 60 * 60 * 1000));
    
    return { daysUntilConversion, estimatedDate, averageStampsPerDay };
  };

  const getTrialProgressIndicator = (subscription: Subscription) => {
    if (subscription.status !== 'FREE_TRIAL' || typeof subscription.stampCount !== 'number') {
      return '-';
    }

    const maxStamps = 500;
    const progress = Math.min((subscription.stampCount / maxStamps) * 100, 100);
    const isNearCompletion = progress >= 80;
    const conversionEstimate = calculateConversionEstimate(subscription);
    
    const progressBar = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            flexGrow: 1,
            height: 8,
            borderRadius: 4,
            backgroundColor: 'grey.200',
            '& .MuiLinearProgress-bar': {
              backgroundColor: isNearCompletion ? 'warning.main' : 'primary.main'
            },
            cursor: conversionEstimate ? 'pointer' : 'default'
          }}
          onClick={conversionEstimate ? (event) => {
            setConversionPopoverAnchor(event.currentTarget);
            setSelectedTrialSubscription(subscription);
          } : undefined}
        />
        <Tooltip title={`${subscription.stampCount} of ${maxStamps} stamps used`} arrow>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 35 }}>
            {subscription.stampCount}/{maxStamps}
          </Typography>
        </Tooltip>
      </Box>
    );

    return progressBar;
  };

  // Calculate weekly conversion estimates
  const getWeeklyConversionEstimates = () => {
    const freeTrialSubs = subscriptions.filter(s => s.status === 'FREE_TRIAL' && typeof s.stampCount === 'number');
    const nextWeekConversions = freeTrialSubs.filter(subscription => {
      const estimate = calculateConversionEstimate(subscription);
      return estimate && estimate.daysUntilConversion <= 7 && estimate.daysUntilConversion > 0;
    });

    return {
      totalFreeTrials: freeTrialSubs.length,
      convertingNextWeek: nextWeekConversions.length,
      conversions: nextWeekConversions
    };
  };

  const weeklyEstimates = getWeeklyConversionEstimates();

  // Calculate Monthly Recurring Revenue (MRR)
  // Sum amounts where both BrewPrks status and PayFast status are ACTIVE
  const mrrSubscriptions = subscriptions.filter(s => 
    s.status === 'ACTIVE' && 
    s.pf_status === 'ACTIVE'
  );
  const monthlyRecurringRevenue = mrrSubscriptions.reduce((total, subscription) => {
    const amount = subscription.pf_amount || 0;
    const amountAfterFees = (amount / 100) * 0.9586; // Subtract 4.14% payment provider fees
    return total + amountAfterFees;
  }, 0);

  // Calculate Free Trial Revenue Potential
  // These are subscriptions with FREE_TRIAL BrewPrks status and ACTIVE PayFast status
  const freeTrialSubscriptions = subscriptions.filter(s => 
    s.status === 'FREE_TRIAL' && 
    s.pf_status === 'ACTIVE'
  );
  
  // Calculate potential revenue using actual amounts from free trial subscriptions
  const potentialFreeTrialRevenue = freeTrialSubscriptions.reduce((total, subscription) => {
    const amount = subscription.pf_amount || 0;
    const amountAfterFees = (amount / 100) * 0.9586; // Subtract 4.14% payment provider fees
    return total + amountAfterFees;
  }, 0);

  // Calculate fees subtracted for transparency
  const currentMrrFees = mrrSubscriptions.reduce((total, subscription) => {
    const amount = subscription.pf_amount || 0;
    const fees = (amount / 100) * 0.0414; // 4.14% fees
    return total + fees;
  }, 0);

  const potentialMrrFees = freeTrialSubscriptions.reduce((total, subscription) => {
    const amount = subscription.pf_amount || 0;
    const fees = (amount / 100) * 0.0414; // 4.14% fees
    return total + fees;
  }, 0);

  const getSubscriptionType = (subscription: Subscription) => {
    if (subscription.pf_status !== null) {
      return { label: 'PayFast', color: 'primary' as const };
    } else if (subscription.subscription_token.includes('tmp')) {
      return { label: 'Manual', color: 'secondary' as const };
    } else {
      return { label: 'Local', color: 'default' as const };
    }
  };

  if (isPending) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  // Show loading spinner while authentication is in progress
  if (user.loading || !user.loggedIn) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUp color="primary" sx={{ mr: 2 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h4" color="primary">
                    R{monthlyRecurringRevenue.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      MRR
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(event) => setMrrPopoverAnchor(event.currentTarget)}
                      sx={{ ml: 0.5, p: 0.25 }}
                    >
                      <HelpOutline fontSize="small" color="action" />
                    </IconButton>
                  </Box>
                </Box>
                <IconButton
                  onClick={() => setMrrExpanded(!mrrExpanded)}
                  size="small"
                  sx={{
                    transform: mrrExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease-in-out'
                  }}
                >
                  <ExpandMore />
                </IconButton>
              </Box>
              
              <Collapse in={mrrExpanded}>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Revenue Potential
                  </Typography>
                  
                  <List dense sx={{ py: 0 }}>
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <Box display="flex" justifyContent="space-between" width="100%">
                        <Typography variant="body2" color="text.secondary">
                          Active MRR Subscriptions:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {mrrSubscriptions.length}
                        </Typography>
                      </Box>
                    </ListItem>
                    
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <Box display="flex" justifyContent="space-between" width="100%">
                        <Typography variant="body2" color="text.secondary">
                          Payment Fees (~4.14%):
                        </Typography>
                        <Typography variant="body2" fontWeight="medium" color="error.main">
                          -R{currentMrrFees.toFixed(2)}
                        </Typography>
                      </Box>
                    </ListItem>
                    
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <Box display="flex" justifyContent="space-between" width="100%">
                        <Typography variant="body2" color="text.secondary">
                          Free Trial Accounts:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {freeTrialSubscriptions.length}
                        </Typography>
                      </Box>
                    </ListItem>

                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <Box display="flex" justifyContent="space-between" width="100%">
                        <Typography variant="body2" color="text.secondary">
                          Converting this week:
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {weeklyEstimates.convertingNextWeek}
                        </Typography>
                      </Box>
                    </ListItem>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <Box display="flex" justifyContent="space-between" width="100%">
                        <Typography variant="body2" color="primary" fontWeight="medium">
                          Potential Additional MRR:
                        </Typography>
                        <Typography variant="body2" color="primary" fontWeight="bold">
                          R{potentialFreeTrialRevenue.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    </ListItem>
                    
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <Box display="flex" justifyContent="space-between" width="100%">
                        <Typography variant="body2" color="text.secondary">
                          Est. Additional Fees (~4.14%):
                        </Typography>
                        <Typography variant="body2" fontWeight="medium" color="error.main">
                          -R{potentialMrrFees.toFixed(2)}
                        </Typography>
                      </Box>
                    </ListItem>
                    
                    <Divider sx={{ my: 1 }} />
                    
                    <ListItem sx={{ px: 0, py: 0.5 }}>
                      <Box display="flex" justifyContent="space-between" width="100%">
                        <Typography variant="body2" color="success.main" fontWeight="medium">
                          Total Potential MRR:
                        </Typography>
                        <Typography variant="body2" color="success.main" fontWeight="bold">
                          R{(monthlyRecurringRevenue + potentialFreeTrialRevenue).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Typography>
                      </Box>
                    </ListItem>
                  </List>
                  
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    *All amounts shown are net revenue after 4.14% payment provider fees have been deducted <br />(3.2% + R2 + 15% tax on every transaction)
                  </Typography>
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CheckCircle color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" color="success.main">
                    {subscriptions.filter(s => 
                      s.status === 'FREE_TRIAL' || s.status === 'ACTIVE'
                    ).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    Live Stores
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Payment color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" color="info.main">
                    {subscriptions.filter(s => 
                      s.status === 'ACTIVE' && s.pf_status === 'ACTIVE'
                    ).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Paying Customers
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Edit color="secondary" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" color="secondary.main">
                    {subscriptions.filter(s => s.status === 'FREE_TRIAL').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Free Trial Accounts
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <Cancel color="error" sx={{ mr: 2 }} />
                <Box>
                  <Typography variant="h4" color="error.main">
                    {subscriptions.filter(s => s.pf_status === 'CANCELLED').length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cancelled
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* MRR Popover */}
      <Popover
        open={Boolean(mrrPopoverAnchor)}
        anchorEl={mrrPopoverAnchor}
        onClose={() => setMrrPopoverAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, maxWidth: 300 }}>
          <Typography variant="subtitle2" gutterBottom>
            Monthly Recurring Revenue (MRR)
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This is a strict sum of all subscription amounts where both BrewPrks Status and PayFast Status are "ACTIVE". Only fully active, revenue-generating subscriptions are included in this calculation.
          </Typography>
        </Box>
      </Popover>

      {/* Conversion Estimate Popover */}
      <Popover
        open={Boolean(conversionPopoverAnchor)}
        anchorEl={conversionPopoverAnchor}
        onClose={() => {
          setConversionPopoverAnchor(null);
          setSelectedTrialSubscription(null);
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {selectedTrialSubscription && typeof selectedTrialSubscription.stampCount === 'number' && (
          <Box sx={{ p: 2, maxWidth: 280 }}>
            <Typography variant="subtitle2" gutterBottom>
              Conversion Estimate
            </Typography>
            {(() => {
              const estimate = calculateConversionEstimate(selectedTrialSubscription);
              if (!estimate || !estimate.estimatedDate) {
                return (
                  <Typography variant="body2" color="text.secondary">
                    Unable to estimate conversion date (no stamp activity detected)
                  </Typography>
                );
              }
              
              const { daysUntilConversion, estimatedDate, averageStampsPerDay } = estimate;
              const formattedDate = estimatedDate.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: '2-digit'
              });
              
              return (
                <Box>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Est conversion in {daysUntilConversion} days
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {formattedDate}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary" display="block">
                    Avg: {averageStampsPerDay.toFixed(1)} stamps/day
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Remaining: {500 - selectedTrialSubscription.stampCount} stamps
                  </Typography>
                </Box>
              );
            })()}
          </Box>
        )}
      </Popover>

      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Subscription Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Overview of all merchant subscriptions and payment status
            </Typography>
            {dataAgeSeconds !== null && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Data cached {Math.floor(dataAgeSeconds / 60)}m {dataAgeSeconds % 60}s ago
              </Typography>
            )}
          </Box>
          <Button
            startIcon={<Refresh />}
            onClick={handleRefresh}
            variant="outlined"
            disabled={isLoading}
            sx={{ ml: 2 }}
          >
            {isLoading ? 'Refreshing...' : 'Refresh Data'}
          </Button>
        </Box>

        {/* Filters Section */}
        <Box sx={{ mb: 3, mt: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <FilterList color="action" />
              <Typography variant="h6" color="text.secondary">
                Filters:
              </Typography>
            </Box>
            
            <FormControl sx={{ minWidth: 200, mb: 1 }}>
              <InputLabel>BrewPrks Status</InputLabel>
              <Select
                multiple
                value={brewprksStatusFilter}
                onChange={handleBrewprksStatusFilterChange}
                input={<OutlinedInput label="BrewPrks Status" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {uniqueBrewprksStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox checked={brewprksStatusFilter.indexOf(status) > -1} />
                    <ListItemText primary={status} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 200, mb: 1 }}>
              <InputLabel>PayFast Status</InputLabel>
              <Select
                multiple
                value={payfastStatusFilter}
                onChange={handlePayfastStatusFilterChange}
                input={<OutlinedInput label="PayFast Status" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} size="small" />
                    ))}
                  </Box>
                )}
              >
                {allPayfastStatuses.map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox checked={payfastStatusFilter.indexOf(status) > -1} />
                    <ListItemText primary={status} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {hasActiveFilters && (
              <Button
                startIcon={<Clear />}
                onClick={clearAllFilters}
                variant="outlined"
                size="small"
                sx={{ mb: 1 }}
              >
                Clear Filters
              </Button>
            )}
          </Stack>

          {hasActiveFilters && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {filteredAndSortedSubscriptions.length} of {subscriptions.length} subscriptions
              </Typography>
            </Box>
          )}
        </Box>
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortColumn === 'merchantName'}
                    direction={sortColumn === 'merchantName' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('merchantName')}
                  >
                    Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortColumn === 'merchantId'}
                    direction={sortColumn === 'merchantId' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('merchantId')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortColumn === 'type'}
                    direction={sortColumn === 'type' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('type')}
                  >
                    Type
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortColumn === 'brewprksStatus'}
                    direction={sortColumn === 'brewprksStatus' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('brewprksStatus')}
                  >
                    BrewPrks Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortColumn === 'payfastStatus'}
                    direction={sortColumn === 'payfastStatus' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('payfastStatus')}
                  >
                    PayFast Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortColumn === 'amount'}
                    direction={sortColumn === 'amount' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('amount')}
                  >
                    Amount
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortColumn === 'billingDate'}
                    direction={sortColumn === 'billingDate' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('billingDate')}
                  >
                    Next Billing
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortColumn === 'createdAt'}
                    direction={sortColumn === 'createdAt' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('createdAt')}
                  >
                    Created
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortColumn === 'updatedAt'}
                    direction={sortColumn === 'updatedAt' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('updatedAt')}
                  >
                    Updated
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortColumn === 'trialProgress'}
                    direction={sortColumn === 'trialProgress' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('trialProgress')}
                  >
                    Trial Progress
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAndSortedSubscriptions.map((subscription) => (
                <TableRow key={subscription.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {subscription.merchant.name}
                    </Typography>
                  </TableCell>
                  <TableCell>{subscription.merchant_id}</TableCell>
                  <TableCell>
                    <Chip
                      label={getSubscriptionType(subscription).label}
                      color={getSubscriptionType(subscription).color}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {getBrewPrksStatusChip(subscription)}
                  </TableCell>
                  <TableCell>
                    {getPayfastStatusChip(subscription)}
                  </TableCell>
                  <TableCell>
                    {subscription.pf_amount !== null ? 
                      formatAmount(subscription.pf_amount) : 
                      '-'
                    }
                  </TableCell>
                  <TableCell>
                    {formatDate(subscription.billing_date)}
                  </TableCell>
                  <TableCell>
                    {formatDate(subscription.createdAt)}
                  </TableCell>
                  <TableCell>
                    {formatDate(subscription.updatedAt)}
                  </TableCell>
                  <TableCell>
                    {getTrialProgressIndicator(subscription)}
                  </TableCell>
                </TableRow>
              ))}
              {filteredAndSortedSubscriptions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <Typography variant="body1" color="text.secondary">
                      {subscriptions.length === 0 
                        ? "No subscription data available"
                        : "No subscriptions match the selected filters"
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default SubscriptionAdminPage; 